import { useMutation, useQuery } from "@apollo/client/react";
import { EyeIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ArrowPathIcon, PlusCircleIcon } from "@heroicons/react/24/solid";
import { ColumnDef, getCoreRowModel } from "@tanstack/react-table";
import { Fragment, useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ButtonDots, itemClasses } from "../../../../../components/appearance";
import { ErrorFallback, Head } from "../../../../../components/core";
import { Button, FieldDriver, FieldUser } from "../../../../../components/form";
import { LimitBy, TableCursor } from "../../../../../components/table";
import { VehicleBasic } from "../../../../../graphql/fleets/vehicles";
import {
  GET_VEHICLEISSUE_BY_VEHICLE_ID,
  REMOVE_VEHICLEISSUE,
  renderPriority,
  renderStatus,
  VehicleIssue,
  VehicleIssueStatus,
} from "../../../../../graphql/fleets/vehicles/pages/issues";
import { AlertModal, AlertType, useAlert } from "../../../../../hooks/useAlert";
import { classNames, formatDate } from "../../../../../utils";
import { AuthShield } from "../../../../auth/core";

const IssuesList = ({
  breadcrumbs,
  vehicleBasic,
}: {
  breadcrumbs: Breadcrumb[];
  vehicleBasic: VehicleBasic | null;
}) => {
  let { vehicleId } = useParams();
  const { t } = useTranslation();
  const [refetching, setRefetching] = useState<boolean>(false);
  const [pageLimit, setPageLimit] = useState<LimitBy>(LimitBy.L3);

  const { data, loading, error, refetch } = useQuery<{
    fetchVehicleIssuesByVehicle: VehicleIssue[];
  }>(GET_VEHICLEISSUE_BY_VEHICLE_ID, {
    variables: {
      vehicleId,
    },
  });
  const issues = useMemo<VehicleIssue[]>(() => {
    const updatedIssue = data?.fetchVehicleIssuesByVehicle?.length
      ? data?.fetchVehicleIssuesByVehicle.map((item) => {
          return {
            ...item,
            reportedDate: formatDate(item.reportedDate),
            overDueDate: formatDate(item.overDueDate),
            createdAt: formatDate(item.createdAt),
          };
        })
      : [];
    return updatedIssue;
  }, [data]);

  const [deleteVehicleIssue] = useMutation(REMOVE_VEHICLEISSUE);
  const handleDelete = async (id: string) => {
    deleteVehicleIssue({
      variables: {
        id: Number(id),
      },
    })
      .then(async ({ data }) => {
        if (data?.vehicleIssueDelete) {
          await refetch();
          toast.success(`Vehicle issue deleted successfully`);
        } else {
          toast.error("Something went wrong, please try again later");
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  type AlertProps = {
    id: string;
    name: string;
  };
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const ResponseAlert = useAlert({
    open: alert ? true : false,
    title: "Do you want to proceed with this operation?",
    message: (
      <p className="text-sm text-gray-500">
        Are you sure you wish to delete{" "}
        <b className="text-gray-900">{alert?.name}</b> permanently. This action
        can't be undone. All records related to{" "}
        <b className="text-gray-900">{alert?.name}</b> will be deleted.
      </p>
    ),
    type: AlertType.DANGER,
    modal: AlertModal.CENTERED_DOUBLE_ACTION,
    delay: 3000,
    onConfirm: async () => {
      await handleDelete(alert?.id!);
      setAlert(null);
    },
    onCancel: () => {
      setAlert(null);
    },
  });

  const columns = useMemo<ColumnDef<VehicleIssue, any>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 30,
        enableHiding: false,
      },
      {
        accessorKey: "priority",
        header: "Priority",
        cell: (props) => (
          <Link
            to={`/fleets/vehicles/${vehicleId}/issue/${props.row.original.id}`}
          >
            {renderPriority(props.row.original.priority)}
          </Link>
        ),
      },
      {
        accessorKey: "summary",
        header: "Summary",
        size: 300,
      },
      {
        accessorKey: "status",
        header: "Issue Status",
        cell: (props) => renderStatus(props.row.original.status),
      },
      {
        accessorKey: "reportedDate",
        header: "Reported Date",
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
      },
      {
        accessorKey: "assignedToId",
        header: "Assigned",
        cell: (props) => (
          <FieldUser
            title="User"
            value={props.row.original.assignedToId}
            isPreview
          />
        ),
      },
      {
        accessorKey: "reportedById",
        header: "Reported",
        cell: (props) => (
          <FieldDriver
            title="Driver"
            value={props.row.original.reportedById}
            isPreview
          />
        ),
      },
      {
        accessorKey: "overDueDate",
        header: "Due Date",
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enableHiding: false,
        enableGlobalFilter: false,
        size: 220,
        cell: (props) => (
          <ButtonDots>
            <AuthShield access={["read-vehicles"]}>
              <Button
                variant="text"
                className={itemClasses}
                type="link"
                href={`/fleets/vehicles/${vehicleId}/issue/${props.row.original.id}`}
              >
                <div className="flex items-center">
                  <EyeIcon
                    aria-hidden="true"
                    className="mr-2 h-5 w-5 text-current"
                  />
                  <span className="block truncate">
                    View
                    <span className="sr-only">
                      , {props.row.original.issueType}
                    </span>
                  </span>
                </div>
              </Button>
            </AuthShield>
            {props.row.original.status !== VehicleIssueStatus.RESOLVED ? (
              <Fragment>
                <AuthShield access={["update-vehicles"]}>
                  <Button
                    variant="text"
                    className={itemClasses}
                    type="link"
                    href={`/fleets/vehicles/${vehicleId}/issue/${props.row.original.id}/edit`}
                  >
                    <div className="flex items-center">
                      <PencilIcon
                        aria-hidden="true"
                        className="mr-2 h-5 w-5 text-current"
                      />
                      <span className="block truncate">
                        Edit
                        <span className="sr-only">
                          , {props.row.original.issueType}
                        </span>
                      </span>
                    </div>
                  </Button>
                </AuthShield>
                <AuthShield access={["destroy-vehicles"]}>
                  <Button
                    variant="text"
                    className={itemClasses}
                    onClick={() => {
                      setAlert({
                        id: props.row.original.id!,
                        name: props.row.original.issueType,
                      });
                    }}
                  >
                    <div className="flex items-center">
                      <TrashIcon
                        aria-hidden="true"
                        className="mr-2 h-5 w-5 text-current"
                      />
                      <span className="block truncate">
                        Delete
                        <span className="sr-only">
                          , {props.row.original.issueType}
                        </span>
                      </span>
                    </div>
                  </Button>
                </AuthShield>
              </Fragment>
            ) : null}
          </ButtonDots>
        ),
      },
    ],
    [vehicleId]
  );

  const handleRefetch = useCallback(() => {
    setRefetching(true);
    refetch().finally(() => {
      setRefetching(false);
    });
  }, [refetch]);

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title={IssuesListResource.name}
        heading={IssuesListResource.name}
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: IssuesListResource.name,
            href: null,
          },
        ]}
      />
      <ResponseAlert />
      <div className="py-6 sm:py-8">
        <div className="rounded-xl bg-greyish p-5">
          <div className="mb-6 sm:flex sm:items-center md:mb-8">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-medium text-gray-900">
                {t("heading_vehicle_issues")}
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                {t("description_vehicle_issues")}
              </p>
            </div>
            <div className="ml-auto flex items-center space-x-2 pr-3">
              <AuthShield access={["create-vehicles"]}>
                <Button
                  type="link"
                  href={`/fleets/vehicles/${vehicleId}/issue/add`}
                >
                  <PlusCircleIcon className="mr-2 h-5 w-5" />
                  Add issue
                </Button>
              </AuthShield>
              <Button
                variant="icon"
                onClick={handleRefetch}
                disabled={loading}
                className="flex h-9 items-center justify-center px-2 text-blue-700"
              >
                <ArrowPathIcon
                  aria-hidden="true"
                  className={classNames(
                    "h-5 w-5",
                    refetching ? "animate-spin" : ""
                  )}
                />
                <span className="sr-only">Refresh issue list</span>
              </Button>
            </div>
          </div>
          <TableCursor
            data={issues}
            columns={columns}
            loading={loading}
            totalRows={issues.length}
            pageLimit={pageLimit}
            setPageLimit={setPageLimit}
            enableRowSelection={false}
            getCoreRowModel={getCoreRowModel()}
            enableGlobalFilter={true}
            fallbackTitle="No issues found"
            fallbackText="You can add a new issue by clicking on add issue button."
          />
        </div>
      </div>
    </>
  );
};

export default IssuesList;
export const IssuesListResource: ResourceProps = {
  name: "Issues",
  description: "List of all vehicle issues",
  access: ["update-vehicles"],
  path: "/issue",
  icon: "bi bi-person",
};
